import { useEffect, useState } from 'react';
import client from '../api/client';
import Layout from '../components/Layout';
import './Inventory.css';

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [countModal, setCountModal] = useState(null);
  const [countQty, setCountQty] = useState('');
  const [form, setForm] = useState({
    item_name: '', sku: '', quantity: '', unit: '', location_id: '', notes: '',
  });
  const [error, setError] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [invRes, locRes] = await Promise.all([
        client.get('/inventory'),
        client.get('/locations'),
      ]);
      setItems(invRes.data);
      setLocations(locRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await client.post('/inventory', {
        ...form,
        quantity: form.quantity ? parseInt(form.quantity) : 0,
      });
      setShowModal(false);
      setForm({ item_name: '', sku: '', quantity: '', unit: '', location_id: '', notes: '' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create item');
    }
  };

  const handleCount = async (e) => {
    e.preventDefault();
    try {
      await client.post(`/inventory/${countModal.id}/scan`, {
        quantity: parseInt(countQty),
      });
      setCountModal(null);
      setCountQty('');
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await client.delete(`/inventory/${id}`);
      loadData();
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = items.filter(i =>
    i.item_name.toLowerCase().includes(search.toLowerCase()) ||
    (i.sku && i.sku.toLowerCase().includes(search.toLowerCase()))
  );

  const groupedByLocation = () => {
    const groups = {};
    filtered.forEach(item => {
      const key = item.location_name || 'Unassigned';
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return groups;
  };

  if (loading) return <Layout><div style={{color:'#6b7280', padding:'20px'}}>Loading...</div></Layout>;

  return (
    <Layout>
      <div className="inv-header">
        <h1>Inventory</h1>
        <div className="inv-header-right">
          <div className="inv-toggle">
            <button
              className={view === 'list' ? 'inv-toggle-btn active' : 'inv-toggle-btn'}
              onClick={() => setView('list')}
            >List</button>
            <button
              className={view === 'audit' ? 'inv-toggle-btn active' : 'inv-toggle-btn'}
              onClick={() => setView('audit')}
            >Floor Audit</button>
          </div>
          <button className="inv-btn-new" onClick={() => setShowModal(true)}>+ New Item</button>
        </div>
      </div>

      <div className="inv-search-wrap">
        <input
          className="inv-search"
          placeholder="Search by name or SKU..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {view === 'list' && (
        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th>Item Name</th>
                <th>SKU</th>
                <th>Location</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Last Counted</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan="7" className="inv-empty">No items found.</td>
                </tr>
              )}
              {filtered.map(item => (
                <tr key={item.id}>
                  <td className="inv-name">{item.item_name}</td>
                  <td className="inv-sku">{item.sku || '--'}</td>
                  <td className="inv-loc">{item.location_name || 'Unassigned'}</td>
                  <td className="inv-qty">{item.quantity}</td>
                  <td className="inv-unit">{item.unit || '--'}</td>
                  <td className="inv-scanned">
                    {item.last_scanned_at
                      ? new Date(item.last_scanned_at).toLocaleString()
                      : <span className="inv-never">Never counted</span>
                    }
                  </td>
                  <td className="inv-actions">
                    <button className="inv-btn-count" onClick={() => { setCountModal(item); setCountQty(item.quantity); }}>Count</button>
                    <button className="inv-btn-delete" onClick={() => deleteItem(item.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'audit' && (
        <div className="inv-audit">
          {Object.entries(groupedByLocation()).map(([locationName, locationItems]) => (
            <div key={locationName} className="inv-audit-group">
              <div className="inv-audit-location">{locationName}</div>
              {locationItems.map(item => (
                <div key={item.id} className="inv-audit-item">
                  <div className="inv-audit-item-info">
                    <div className="inv-audit-item-name">{item.item_name}</div>
                    <div className="inv-audit-item-meta">
                      {item.sku && <span>{item.sku} · </span>}
                      <span className="inv-audit-qty">{item.quantity} {item.unit || 'units'}</span>
                      {item.last_scanned_at
                        ? <span> · counted {new Date(item.last_scanned_at).toLocaleDateString()}</span>
                        : <span className="inv-never"> · never counted</span>
                      }
                    </div>
                  </div>
                  <button
                    className="inv-audit-count-btn"
                    onClick={() => { setCountModal(item); setCountQty(item.quantity); }}
                  >
                    Count
                  </button>
                </div>
              ))}
            </div>
          ))}
          {Object.keys(groupedByLocation()).length === 0 && (
            <div className="inv-empty-audit">No items found.</div>
          )}
        </div>
      )}

      {showModal && (
        <div className="inv-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="inv-modal" onClick={e => e.stopPropagation()}>
            <h2>New Item</h2>
            {error && <div className="inv-modal-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <input placeholder="Item Name *" value={form.item_name} onChange={e => setForm({...form, item_name: e.target.value})} required />
              <input placeholder="SKU" value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} />
              <input type="number" placeholder="Quantity" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} />
              <input placeholder="Unit (e.g. sheets, boxes)" value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} />
              <select value={form.location_id} onChange={e => setForm({...form, location_id: e.target.value})}>
                <option value="">No Location</option>
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
              <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              <div className="inv-modal-actions">
                <button type="button" className="inv-btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="inv-btn-new">Add Item</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {countModal && (
        <div className="inv-modal-overlay" onClick={() => setCountModal(null)}>
          <div className="inv-modal" onClick={e => e.stopPropagation()}>
            <h2>Count Item</h2>
            <div className="inv-scan-name">{countModal.item_name}</div>
            <form onSubmit={handleCount}>
              <input
                type="number"
                placeholder="Current quantity"
                value={countQty}
                onChange={e => setCountQty(e.target.value)}
                required
                autoFocus
              />
              <div className="inv-modal-actions">
                <button type="button" className="inv-btn-cancel" onClick={() => setCountModal(null)}>Cancel</button>
                <button type="submit" className="inv-btn-new">Confirm Count</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}